import React, { useState, useEffect, useCallback } from 'react'
import { Select } from 'antd'
import { AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import taskTypeChangeApi, { type ConversionOptions } from '../../../services/taskTypeChangeApi'

interface TaskActionSelectProps {
  taskDbId: number
  /** Falls back to this when the task id is unknown or the value is unchanged. */
  value?: string | null
  onConverted?: () => void | Promise<void>
  options: Array<{ value: string; label: string }>
}

const LABEL: Record<string, string> = { 'live-load': 'Live Load', drop: 'Drop' }

/**
 * The Task Action dropdown.
 *
 * This field used to be a dead control: it posted `task_subtype` to the task
 * update endpoint, which silently drops it (the column is derived from the
 * order's type and is excluded from that endpoint's field whitelist). Dispatchers
 * could pick a value, hit Save, get a success toast, and nothing changed.
 *
 * Now it drives the real conversion, which is more than a column write — it
 * rebuilds the route, flips the order type so the follow-up return task is or is
 * not generated, and reprices the driver. So changing it asks for confirmation
 * and a reason, and refuses with a specific explanation when the task is past the
 * point where switching is meaningful.
 */
export const TaskActionSelect: React.FC<TaskActionSelectProps> = ({
  taskDbId, value, onConverted, options,
}) => {
  const [conv, setConv] = useState<ConversionOptions | null>(null)
  const [pending, setPending] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    if (!taskDbId) return
    try {
      setConv(await taskTypeChangeApi.getConversionOptions(taskDbId))
    } catch {
      setConv(null)
    }
  }, [taskDbId])

  useEffect(() => { load() }, [load])

  // Show what the server says the task is, not local edit state — a refused
  // conversion must never leave the field displaying something that is not true.
  const current = conv?.currentSubtype ?? value ?? undefined

  const handleSelect = (next: string) => {
    if (!next || next === current) return
    if (!conv) { toast.error("Still loading this task's options — try again in a moment."); return }
    if (next !== conv.targetSubtype) {
      toast.error(`Task is already ${LABEL[conv.currentSubtype] || conv.currentSubtype}.`)
      return
    }
    if (!conv.canConvert) {
      // The whole point of the message: tell them what to do about it.
      toast.error(conv.blockedReasons[0] || 'This task cannot be switched.', {
        description: conv.blockedReasons.slice(1).join(' ') || undefined,
        duration: 6000,
      })
      return
    }
    setPending(next)
  }

  const confirm = async () => {
    if (!pending || !reason.trim() || !conv) return
    setSubmitting(true)
    try {
      const result = await taskTypeChangeApi.convert(taskDbId, {
        reason: reason.trim(),
        targetSubtype: pending as 'live-load' | 'drop',
        expectedVersion: conv.stateVersion,
      })
      toast.success([
        `Switched to ${LABEL[pending]}`,
        result.autoCompleted && 'task marked completed',
        result.orderChanged && `order ${result.orderChanged.orderNumber} → ${result.orderChanged.to}`,
        result.removedFollowUpTasks?.length && `withdrew return task ${result.removedFollowUpTasks.join(', ')}`,
      ].filter(Boolean).join(' · '))
      setPending(null); setReason('')
      await onConverted?.()
      await load()
    } catch (e) {
      const message = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (e instanceof Error ? e.message : 'Switch failed')
      toast.error(message, { duration: 6000 })
      await load()
    } finally {
      setSubmitting(false)
    }
  }

  const effects = conv?.effects
  const blocked = conv && !conv.canConvert

  return (
    <>
      <Select
        value={current}
        onChange={handleSelect}
        placeholder="Select action"
        style={{ width: '100%' }}
        status={blocked ? 'warning' : undefined}
      >
        {options.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
      </Select>

      {blocked && (
        <div className="mt-1 flex items-start gap-1 text-[11px] text-amber-700">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{conv.blockedReasons[0]}</span>
        </div>
      )}

      {pending && effects && (
        <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2 space-y-2">
          <div className="text-[11px] text-gray-600 space-y-0.5">
            {effects.removesReturnNode && <div>· Route drops the return leg — task ends at the warehouse ({effects.routeNodesAfter} stops)</div>}
            {effects.addsReturnNode && <div>· Route gains a return leg — driver takes the container back ({effects.routeNodesAfter} stops)</div>}
            {effects.autoCompletes && (
              <div className="text-amber-700"><strong>· This task will be marked completed</strong> — load/unload is already done</div>
            )}
            {effects.orderTypeChange && (
              <div>· Order {effects.orderTypeChange.orderNumber} becomes {effects.orderTypeChange.to}
                {effects.orderTypeChange.affectsTasks > 1 && ` (${effects.orderTypeChange.affectsTasks} tasks; unstarted ones follow)`}
              </div>
            )}
            {effects.createsFollowUpTask && <div>· A return task will be generated on completion</div>}
            {effects.removesFollowUpTask && <div>· The unstarted return task will be withdrawn</div>}
            {effects.salaryRecalculated && <div>· Driver pay switches rate card (waiting time still paid from timestamps)</div>}
          </div>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Reason (required)"
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setPending(null); setReason('') }}
              className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100">
              Cancel
            </button>
            <button type="button" disabled={!reason.trim() || submitting} onClick={confirm}
              className="px-2 py-1 text-xs rounded bg-gray-900 text-white hover:bg-gray-800
                         disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1">
              {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
              Confirm
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default TaskActionSelect
